import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Delete, Edit, People } from '@mui/icons-material';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import type { PrototypeFirmware } from '@/models/PrototypeFirmware/PrototypeFirmware';
import type { PrototyperUserInfo } from '@/models/PrototypeFirmware/PrototyperUserInfo';
import COLORS from '@/resources/style/Themes';
import AddPrototypeFirmwareModal from '../modals/AddPrototypeFirmwareModal';
import EditPrototypeFirmwareModal from '../modals/EditPrototypeFirmwareModal';
import AssignUserModal from '../modals/AssignUserModal';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import {
  deletePrototypeFirmware,
  formatPrototypeFirmwareError,
} from '@/repository/PrototypeFirmwareDAO';
import { useToast, ToastType } from '@/context_providers/toast_context/ToastContext';

type Props = {
  firmwares: PrototypeFirmware[];
  prototypers: PrototyperUserInfo[];
  addModalOpen: boolean;
  onAddModalClose: () => void;
  refetchFirmwares: () => Promise<void>;
  refetchPrototypers: () => Promise<void>;
};

const formatTimestamp = (tsSeconds: number) => {
  const d = new Date(tsSeconds * 1000);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const FirmwareTab: React.FC<Props> = ({
  firmwares,
  prototypers,
  addModalOpen,
  onAddModalClose,
  refetchFirmwares,
  refetchPrototypers,
}) => {
  const { showToast } = useToast();
  const [editing, setEditing] = useState<PrototypeFirmware | null>(null);
  const [assigningToId, setAssigningToId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PrototypeFirmware | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const assigningTo = useMemo(
    () => firmwares.find((firmware) => firmware.id === assigningToId) ?? null,
    [assigningToId, firmwares],
  );

  const handleConfirmDelete = async () => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await deletePrototypeFirmware(deleting.id);
    } catch (error) {
      const { message, shouldRefetch } = formatPrototypeFirmwareError(
        error,
        'Firmware',
      );
      showToast(message, ToastType.DANGER);
      if (shouldRefetch) {
        try {
          await refetchFirmwares();
        } catch {
          // parent's refetch toast covers this
        }
      }
      setDeleteBusy(false);
      return;
    }
    showToast(`Deleted ${deleting.name} v${deleting.version}.`, ToastType.SUCCESS);
    try {
      await refetchFirmwares();
    } catch {
      // refetch failures are surfaced by the parent's own toast.
    }
    setDeleting(null);
    setDeleteBusy(false);
  };

  const columns = useMemo<GridColDef<PrototypeFirmware>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        headerClassName: 'first',
        cellClassName: 'first',
      },
      {
        field: 'version',
        headerName: 'Version',
        flex: 0.7,
        minWidth: 120,
        sortable: true,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.5,
        minWidth: 220,
        renderCell: (params) => (
          <Tooltip title={params.value as string} placement="top-start">
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.value as string}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: '_ts',
        headerName: 'Uploaded',
        flex: 0.9,
        minWidth: 180,
        sortable: true,
        valueFormatter: (value: number) => formatTimestamp(value),
      },
      {
        field: 'assigned_auth_ids',
        headerName: 'Assigned Users',
        flex: 0.7,
        minWidth: 140,
        valueGetter: (_v, row) => row.assigned_auth_ids?.length ?? 0,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.9,
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Tooltip title="Edit firmware" placement="top">
              <IconButton
                size="small"
                onClick={() => setEditing(params.row)}
                aria-label="Edit firmware"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage assigned users" placement="top">
              <IconButton
                size="small"
                onClick={() => setAssigningToId(params.row.id)}
                aria-label="Manage assigned users"
              >
                <People fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete firmware" placement="top">
              <IconButton
                size="small"
                onClick={() => setDeleting(params.row)}
                aria-label="Delete firmware"
                sx={{
                  backgroundColor: COLORS.PINK,
                  '&:hover': { backgroundColor: COLORS.HOVER_RED },
                }}
              >
                <Delete fontSize="small" sx={{ color: COLORS.DARK_RED }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <NavigatorDataGrid<PrototypeFirmware>
        rows={firmwares}
        columns={columns}
        getRowId={(row) => row.id}
        initialPageSize={15}
      />
      <AddPrototypeFirmwareModal
        key={addModalOpen ? 'open' : 'closed'}
        open={addModalOpen}
        onClose={onAddModalClose}
        onUploaded={refetchFirmwares}
      />
      <EditPrototypeFirmwareModal
        key={editing?.id ?? 'closed'}
        firmware={editing}
        onClose={() => setEditing(null)}
        onUpdated={refetchFirmwares}
      />
      <ConfirmModal
        show={!!deleting}
        confirm={handleConfirmDelete}
        onHide={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        confirmTitle={
          deleting ? `Delete ${deleting.name} v${deleting.version}` : ''
        }
        confirmMessage={
          deleting
            ? `Delete '${deleting.name} v${deleting.version}'? This firmware file will be permanently removed.${
                deleting.assigned_auth_ids?.length
                  ? ` ${deleting.assigned_auth_ids.length} user(s) currently assigned will lose access.`
                  : ''
              }`
            : ''
        }
      />
      <AssignUserModal
        firmware={assigningTo}
        prototypers={prototypers}
        onClose={() => setAssigningToId(null)}
        refetchFirmwares={refetchFirmwares}
        refetchPrototypers={refetchPrototypers}
      />
    </Box>
  );
};

export default FirmwareTab;
